import { memo, useRef, useState } from "react"
import { ArticleErpInfo } from "@tm/models"
import { Box, Typography } from "@tm/components"
import { CalcGenArt } from "@bundles/fast-calculator/data/model"
import { createArticleComparer, mapCalcArticleToOePart } from "@bundles/fast-calculator/data/helpers"
import { FastCalculator } from "@tm/data"
import { OeGroups } from "@bundles/fast-calculator/components/_shared/OeGroups"
import { ComposerComponent, ContextComposer } from "../ContextProvider"
import { ArticleListFeaturesDisableProvider } from "../ArticleListConfiguration/ArticleListFeaturesDisable"
import { ArticleListConfigurationProvider } from "../ArticleListConfiguration"
import { ListOptionsProvider } from "../ContextProvider/ListOptions"
import { EmptyListParamsProvider } from "../ContextProvider/ListParams"
import { EmptyFiltersProvider } from "../ContextProvider/Filters"
import { TradeReferenceProvider } from "../ContextProvider/TradeReferences"
import { ErpInfosProvider } from "../ContextProvider/ErpInfos"
import { BasketQuantitiesProvider } from "../ContextProvider/BasketQuantities"
import { ActiveVehicleDataProvidersProvider } from "../ContextProvider/ActiveVehicleDataProviders"
import { NotesProvider } from "../ContextProvider/Notes"
import { ArticleListActionsProvider } from "../ContextProvider/ArticleListActions"
import { FastCalculatorListProvider, useFastCalculatorListContext } from "./ContextProvider/FastList"
import { ProductGroupRepairTimesProvider } from "../ContextProvider/ProductGroupRepairTimes"
import { SupplierLogosProvider } from "../ContextProvider/SupplierLogos"
import { FastCalculatorArticleItem } from "./FastCalculatorArticleItem"
import { FastCalculatorOeItem } from "./FastCalculatorOeItem"
import { NextPageLoader } from "../components/NextPageLoader"

export function FastCalculatorList() {
    const [hasListRendered, setHasListRendered] = useState(false)
    const [erpInfos, setErpInfos] = useState<ArticleErpInfo[]>([]) // provider can hold a global state, where useErpInfos would create a context dependent state

    const components: ComposerComponent[] = [
        [
            ArticleListFeaturesDisableProvider,
            {
                disableSelection: true,
                disableBasketButton: true,
                disableCostEstimationButton: true,
                disableDeleteButton: false,
                disableReplaceWithQuantity: false,
            },
        ],
        [ArticleListConfigurationProvider],
        [EmptyListParamsProvider],
        [EmptyFiltersProvider],
        [FastCalculatorListProvider, { setHasRendered: setHasListRendered, erpInfos }],
        [TradeReferenceProvider, { isEnabled: hasListRendered }],
        [ErpInfosProvider, { isEnabled: hasListRendered, erpInfos, setErpInfos }],
        [BasketQuantitiesProvider, { isEnabled: hasListRendered }],
        [ActiveVehicleDataProvidersProvider],
        [ProductGroupRepairTimesProvider, { isEnabled: false }],
        [NotesProvider, { isEnabled: hasListRendered }],
        [ListOptionsProvider, { isEnabled: hasListRendered }],
        [SupplierLogosProvider, { isEnabled: hasListRendered }],
        [ArticleListActionsProvider],
    ]

    return (
        <ContextComposer components={components}>
            <FastCalculatorListComponent />
        </ContextComposer>
    )
}

const FastCalculatorListComponent = memo(() => {
    const articles = useFastCalculatorListContext((fastCalculatorContext) => fastCalculatorContext.articles)
    const genarts = useFastCalculatorListContext((fastCalculatorContext) => fastCalculatorContext.genarts)
    const fastCalculatorListRef = useRef<HTMLDivElement | null>(null)

    const isPart = (item: FastCalculator.CalcArticle) => {
        return item.type === FastCalculator.ECalcArticle.Article || item.type === FastCalculator.ECalcArticle.AlternativeArticle
    }

    const isOePart = (item: FastCalculator.CalcArticle) => {
        return item.type === FastCalculator.ECalcArticle.OeArticle || item.type === FastCalculator.ECalcArticle.OeAlternativeArticle
    }

    if (!articles.length && !genarts.length) {
        return <NextPageLoader skeletonArticleCount={1} />
    }

    return (
        <>
            {genarts.map((genArt: CalcGenArt) => (
                <Box key={genArt.id ?? genArt.genArtLabel} paddingRight=".7em">
                    <Typography borderBottom="#e2e2e2 solid 2px" padding=".5em 0 .5em .5em" variant="h4">
                        {genArt.genArtLabel}
                    </Typography>
                    {genArt.articles &&
                        genArt.articles.map((article) => {
                            const foundArticle = articles.find(createArticleComparer(article, genArt))
                            return (
                                <Box key={article.id} padding="16px 0 0 16px" ref={fastCalculatorListRef}>
                                    {article.positionLabel && (
                                        <Typography fontSize="12px" fontWeight="600" mb="0.5em">
                                            {article.positionLabel.charAt(0).toUpperCase() + article.positionLabel.slice(1)}
                                        </Typography>
                                    )}
                                    <OeGroups articles={articles} genArt={genArt} item={article} />
                                    {isPart(article) && foundArticle && (
                                        <FastCalculatorArticleItem key={article.id} article={foundArticle} fastCalculatorArticle={article} />
                                    )}
                                    {isOePart(article) && (
                                        <FastCalculatorOeItem oeArticle={mapCalcArticleToOePart(article)} fastCalculatorArticle={article} />
                                    )}
                                </Box>
                            )
                        })}
                </Box>
            ))}
        </>
    )
})
